<template>
    <div>
        <div class="flex items-center mb-30">
            <h2>All Users</h2>
            <a href="/admin/users/create" class="ml-auto btn">
                + <span class="hidden sm:inline-block">new</span> user
            </a>
        </div>

        <table-data-grid v-model="rows" :columns="columns" :default-sort-column="columns[0]" default-sort-direction="desc" @sort="onSort" :select-all="false" class="bg-blue">
            <template v-slot:empty>
                <tr class="h-40">
                    <td class="text-center lead" :colspan="columns.length">No users found. Let's create one!</td>
                </tr>
            </template>

            <template v-slot:default="{selectRow}">
                <tr v-for="(row, index) in rows" :key="row.id" :class="{'bg-blue-lighter': isEven(index)}" class="">
                    <td class="p-10 pl-20">{{ row.full_name }}<span class="block pt-3 leading-tight lg:hidden text-12">{{ row.position }}<br>{{ row.default_client.name }}</span></td>
                    <td class="hidden p-10 lg:table-cell">{{ row.position }}</td>
                    <td class="hidden p-10 lg:table-cell">{{ row.email }}</td>
                    <td class="hidden p-10 lg:table-cell">{{ row.default_client.name }}</td>
                    <td class="hidden p-10 lg:table-cell">{{ row.created_at | morph-date('DD/MM/YY') }}</td>
                    <td class="p-10">
                        <a :href="`/admin/users/${row.id}/edit`" class="flex items-center justify-center w-full h-full group">
                            <svg class="w-18 h-18 fill-white group-hover:fill-cyan" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M491 1536l91-91-235-235-91 91v107h128v128h107zm523-928q0-22-22-22-10 0-17 7l-542 542q-7 7-7 17 0 22 22 22 10 0 17-7l542-542q7-7 7-17zm-54-192l416 416-832 832h-416v-416zm683 96q0 53-37 90l-166 166-416-416 166-165q36-38 90-38 53 0 91 38l235 234q37 39 37 91z"/></svg>
                        </a>
                    </td>
                    <td class="p-10 pr-20">
                        <a v-if="row.deleted_at" @click="onRestore(row)" class="flex items-center justify-center w-full h-full text-center cursor-pointer" title="Restore">
                            <svg class="w-18 h-18 fill-blue-lightest" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M1664 896q0 156-61 298t-164 245-245 164-298 61q-172 0-327-72.5t-264-204.5q-7-10-6.5-22.5t8.5-20.5l137-138q10-9 25-9 16 2 23 12 73 95 179 147t225 52q104 0 198.5-40.5t163.5-109.5 109.5-163.5 40.5-198.5-40.5-198.5-109.5-163.5-163.5-109.5-198.5-40.5q-98 0-188 35.5t-160 101.5l137 138q31 30 14 69-17 40-59 40h-448q-26 0-45-19t-19-45v-448q0-42 40-59 39-17 69 14l130 129q107-101 244.5-156.5t284.5-55.5q156 0 298 61t245 164 164 245 61 298z"/></svg>
                        </a>
                        <a v-else-if="!isThisUser(row)" @click="onDelete(row)" class="flex items-center justify-center w-full h-full text-center cursor-pointer" title="Delete">
                            <svg class="w-18 h-18 fill-blue-lightest hover:fill-coral" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M704 1376v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm-544-992h448l-48-117q-7-9-17-11h-317q-10 2-17 11zm928 32v64q0 14-9 23t-23 9h-96v948q0 83-47 143.5t-113 60.5h-832q-66 0-113-58.5t-47-141.5v-952h-96q-14 0-23-9t-9-23v-64q0-14 9-23t23-9h309l70-167q15-37 54-63t79-26h320q40 0 79 26t54 63l70 167h309q14 0 23 9t9 23z"/></svg>
                        </a>
                        <div v-else class="flex items-center justify-center w-full h-full text-center">
                            <svg class="w-18 h-18 fill-blue" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M704 1376v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm256 0v-704q0-14-9-23t-23-9h-64q-14 0-23 9t-9 23v704q0 14 9 23t23 9h64q14 0 23-9t9-23zm-544-992h448l-48-117q-7-9-17-11h-317q-10 2-17 11zm928 32v64q0 14-9 23t-23 9h-96v948q0 83-47 143.5t-113 60.5h-832q-66 0-113-58.5t-47-141.5v-952h-96q-14 0-23-9t-9-23v-64q0-14 9-23t23-9h309l70-167q15-37 54-63t79-26h320q40 0 79 26t54 63l70 167h309q14 0 23 9t9 23z"/></svg>
                        </div>
                    </td>
                </tr>
            </template>

            <template v-slot:pagination>
                <nav v-if="pagination" class="p-4 font-bold bg-blue-dark text-pink">
                    <pagination
                        :page="currentPage"
                        :total="pagination.last_page"
                        @change="onChangePage"
                    >
                    </pagination>
                </nav>
            </template>
        </table-data-grid>
    </div>
</template>

<script>
import { call, get, sync } from 'vuex-pathify'
import { debounce } from 'lodash'
import { morphDate } from 'vue-morphling'
import Pagination from '../components/dataGrid/theme/pagination'
import StyledCheckbox from '../components/forminputs/styledCheckbox'
import TableDataGrid from '../components/dataGrid/theme/tableDataGrid'
import Vue from 'vue'

Vue.use(morphDate)

export default {
    components: {
        Pagination,
        StyledCheckbox,
        TableDataGrid,
    },

    data() {
        return {
            search: null,
        }
    },

    computed: {
        columns() {
            return [
                {
                    name: 'full_name',
                    label: 'Full Name',
                    classes: 'p-10 pl-20',
                },
                {
                    name: 'position',
                    label: 'Position',
                    classes: 'hidden lg:table-cell p-10',
                },
                {
                    name: 'email',
                    label: 'Email',
                    classes: 'hidden lg:table-cell p-10',
                },
                {
                    name: 'Default Client',
                    sortable: false,
                    classes: 'hidden lg:table-cell p-10',
                },
                {
                    name: 'created_at',
                    label: 'Date Added',
                    classes: 'hidden lg:table-cell p-10',
                },
                {
                    name: 'Edit',
                    sortable: false,
                    classes: 'text-center p-10',
                },
                {
                    name: 'Delete',
                    sortable: false,
                    classes: 'text-center p-10 pr-20',
                },
            ]
        },

        rows: sync('users/users'),
        user: sync('auth/user'),
        creating: sync('users/creating'),
        updating: sync('users/updating'),
        destroying: sync('users/destroying'),
        pagination: sync('users/pagination'),
        currentPage: sync('users/pagination@current_page'),
        sortBy: sync('users/sortBy'),
        filters: sync('users/filters'),
    },
    async mounted() {
        await this.getUsers()
        this.loadClients()
    },
    methods: {
        loadClients: call('clients/loadClients'),
        loadUsers: call('users/loadUsers'),
        destroy: call('users/destroy'),
        searchedUsers: call('users/searchedUsers'),
        restore: call('users/restore'),

        async getUsers() {
            this.$set(this.filters, 'trashed', 'with')

            await this.loadUsers()
        },

        isEven(number) {
            return number % 2 ? false : true
        },

        isThisUser(user) {
            return this.user.id === user.id
        },

        async destroyRows() {
            let destroyed = []
            let rows = this.rows.filter(r => r.selected)

            for (const row of rows) {
                if (row.selected) {
                    destroyed.push(this.destroy(row))
                }
            }

            if (rows.length > 0) {
                await Promise.all(destroyed)

                this.loadUsers()
            }
        },

        onSort({column, direction}) {
            this.currentPage = 1
            this.sortBy = column.name

            if (direction === 'desc') {
                this.sortBy = '-' + column.name
            }

            this.loadUsers()
        },

        async onDelete(user) {
            await this.destroy(user)
            await this.loadUsers()
        },

        onChangePage(page) {
            this.currentPage = page

            this.loadUsers()
        },

        async onRestore(user) {
            await this.restore(user)
            await this.loadUsers()
        },

        onSearch: debounce(function () {
            this.filters = {
                search: this.search,
            }

            this.loadUsers()
        }, 500),
    }
}
</script>
